import { Add, Close, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { useAllCourtRoles, useCreateCourtRole } from '../../Aemter/hooks';
import { useUpdateCourtRole } from '../../hooks/reactQuerys/CourtRoleQueries';
import type { ICourtRole } from '../../Interfaces/ICourtRole';

interface IProps {
  onClose: () => void;
  /** Aktualisiert die (aktiven) Rollen im Elternteil — für die Auswahllisten. */
  onChange: () => void;
}

type Category = 'court' | 'authority';
const CATEGORY_LABEL: Record<Category, string> = {
  court: 'Gericht',
  authority: 'Behörde',
};

/**
 * Rollen-Verwaltung (früher „Gerichtsrollen"). Ein Rollen-Katalog für
 * Gericht- UND Behörden-Rollen:
 *   • Kategorie (Gericht/Behörde) beim Anlegen wählbar und pro Rolle änderbar
 *     — sie steuert, wo die Rolle in den Zuordnungen auftaucht.
 *   • Rollen aktiv/inaktiv schalten — inaktive erscheinen in den
 *     Zuweisungs-Auswahllisten nicht mehr, bleiben aber im Katalog erhalten.
 *
 * Lädt alle Rollen (inkl. inaktiver) über /courtrole/all; onChange stößt im
 * Elternteil das Neuladen der aktiven Rollen an.
 */
export const CourtRolesManager: React.FC<IProps> = ({ onClose, onChange }) => {
  const { showAlert } = useAlert();
  const { allCourtRoles, isLoadingAllCourtRoles, refetchAllCourtRoles } =
    useAllCourtRoles();
  const { createCourtRole, isPendingCreateCourtRole } = useCreateCourtRole();
  const { updateCourtRole, isPendingUpdateCourtRole } = useUpdateCourtRole();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Category | 'all'>('all');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleCategory, setNewRoleCategory] = useState<Category>('court');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingCategory, setEditingCategory] = useState<Category>('court');

  const roles = allCourtRoles ?? [];

  const filtered = useMemo(() => {
    const list = allCourtRoles ?? [];
    const term = search.trim().toLowerCase();
    return list
      .slice()
      .sort((a, b) => a.CourtRole.localeCompare(b.CourtRole))
      .filter(r =>
        categoryFilter === 'all' ? true : r.RoleCategory === categoryFilter
      )
      .filter(r => (term ? r.CourtRole.toLowerCase().includes(term) : true));
  }, [allCourtRoles, search, categoryFilter]);

  const isDuplicate = (name: string, ignoreId?: number): boolean =>
    roles.some(
      r =>
        r.CourtRole.toLowerCase() === name.trim().toLowerCase() &&
        r.idCourtRole !== ignoreId
    );

  const afterChange = () => {
    onChange();
    void refetchAllCourtRoles();
  };

  const handleCreate = async () => {
    const name = newRoleName.trim();
    if (!name || isDuplicate(name)) return;
    try {
      await createCourtRole({
        idCourtRole: -1,
        CourtRole: name,
        activeRole: true,
        RoleCategory: newRoleCategory,
      });
      showAlert({ text: 'Rolle angelegt' });
      setNewRoleName('');
      afterChange();
    } catch {
      showAlert({ severity: 'error', text: 'Fehler beim Anlegen' });
    }
  };

  const startEditing = (role: ICourtRole) => {
    setEditingId(role.idCourtRole);
    setEditingValue(role.CourtRole);
    setEditingCategory(role.RoleCategory);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const saveEditing = async (role: ICourtRole) => {
    const next = editingValue.trim();
    if (!next) {
      cancelEditing();
      return;
    }
    if (isDuplicate(next, role.idCourtRole)) {
      showAlert({ severity: 'error', text: 'Diese Rolle existiert bereits.' });
      return;
    }
    if (next === role.CourtRole && editingCategory === role.RoleCategory) {
      cancelEditing();
      return;
    }
    try {
      await updateCourtRole({
        ...role,
        CourtRole: next,
        RoleCategory: editingCategory,
      });
      showAlert({ text: 'Rolle aktualisiert' });
      cancelEditing();
      afterChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  const toggleActive = async (role: ICourtRole) => {
    try {
      await updateCourtRole({ ...role, activeRole: !role.activeRole });
      showAlert({
        text: role.activeRole ? 'Rolle deaktiviert' : 'Rolle aktiviert',
      });
      afterChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Rollen pflegen
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity='info' variant='outlined'>
            Rollen für <strong>Gerichte</strong> (z. B. Grundbuchamt,
            Handelsregister) und <strong>Behörden</strong> (z. B. Standesamt,
            Finanzamt). Die Kategorie steuert, wo die Rolle in den Zuordnungen
            angeboten wird. <strong>Inaktive</strong> Rollen erscheinen in den
            Auswahllisten nicht mehr, bleiben aber erhalten.
          </Alert>

          {/* Neue Rolle anlegen */}
          <Stack direction='row' spacing={1} alignItems='flex-start'>
            <TextField
              label='Neue Rolle'
              value={newRoleName}
              onChange={e => setNewRoleName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') void handleCreate();
              }}
              placeholder='z. B. Nachlassgericht'
              size='small'
              sx={{ flex: 1 }}
              error={Boolean(newRoleName.trim()) && isDuplicate(newRoleName)}
              helperText={
                Boolean(newRoleName.trim()) && isDuplicate(newRoleName)
                  ? 'Diese Rolle existiert bereits.'
                  : ' '
              }
            />
            <TextField
              select
              label='Kategorie'
              size='small'
              value={newRoleCategory}
              onChange={e => setNewRoleCategory(e.target.value as Category)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value='court'>Gericht</MenuItem>
              <MenuItem value='authority'>Behörde</MenuItem>
            </TextField>
            <Button
              variant='contained'
              startIcon={<Add />}
              onClick={() => void handleCreate()}
              disabled={
                !newRoleName.trim() ||
                isDuplicate(newRoleName) ||
                isPendingCreateCourtRole
              }
              sx={{ mt: 0.25 }}
            >
              Anlegen
            </Button>
          </Stack>

          {/* Filter */}
          <Stack direction='row' spacing={1} alignItems='center'>
            <TextField
              size='small'
              label='Rollen suchen'
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ flex: 1 }}
            />
            <ToggleButtonGroup
              size='small'
              exclusive
              value={categoryFilter}
              onChange={(_, v) => v && setCategoryFilter(v)}
            >
              <ToggleButton value='all'>Alle</ToggleButton>
              <ToggleButton value='court'>Gerichte</ToggleButton>
              <ToggleButton value='authority'>Behörden</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 360,
              overflowY: 'auto',
            }}
          >
            {isLoadingAllCourtRoles ? (
              <Box sx={{ p: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  Keine Rollen gefunden.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filtered.map(r => {
                  const isEditing = editingId === r.idCourtRole;
                  const inactive = r.activeRole === false;
                  return (
                    <ListItem
                      key={r.idCourtRole}
                      divider
                      sx={{
                        opacity: inactive && !isEditing ? 0.55 : 1,
                        gap: 1,
                      }}
                      secondaryAction={
                        isEditing ? (
                          <>
                            <Button
                              size='small'
                              onClick={() => void saveEditing(r)}
                              disabled={isPendingUpdateCourtRole}
                            >
                              Speichern
                            </Button>
                            <Button size='small' onClick={cancelEditing}>
                              Abbruch
                            </Button>
                          </>
                        ) : (
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={0.5}
                          >
                            <Tooltip
                              title={inactive ? 'Aktivieren' : 'Deaktivieren'}
                            >
                              <Switch
                                size='small'
                                checked={!inactive}
                                onChange={() => void toggleActive(r)}
                                disabled={isPendingUpdateCourtRole}
                              />
                            </Tooltip>
                            <Tooltip title='Bearbeiten'>
                              <IconButton
                                size='small'
                                onClick={() => startEditing(r)}
                              >
                                <Edit fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )
                      }
                    >
                      {isEditing ? (
                        <Stack
                          direction='row'
                          spacing={1}
                          sx={{ flex: 1, pr: 12 }}
                        >
                          <TextField
                            size='small'
                            fullWidth
                            autoFocus
                            value={editingValue}
                            onChange={e => setEditingValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') void saveEditing(r);
                              if (e.key === 'Escape') cancelEditing();
                            }}
                          />
                          <TextField
                            select
                            size='small'
                            value={editingCategory}
                            onChange={e =>
                              setEditingCategory(e.target.value as Category)
                            }
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value='court'>Gericht</MenuItem>
                            <MenuItem value='authority'>Behörde</MenuItem>
                          </TextField>
                        </Stack>
                      ) : (
                        <ListItemText
                          primary={
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                            >
                              <span>{r.CourtRole}</span>
                              <Chip
                                label={
                                  CATEGORY_LABEL[r.RoleCategory as Category] ??
                                  r.RoleCategory
                                }
                                size='small'
                                color={
                                  r.RoleCategory === 'court'
                                    ? 'primary'
                                    : 'default'
                                }
                                variant='outlined'
                              />
                              {inactive && (
                                <Chip
                                  label='inaktiv'
                                  size='small'
                                  color='warning'
                                  variant='outlined'
                                />
                              )}
                            </Stack>
                          }
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
